import type { PaginationMetadata, PaginationQuery } from './common'
import type { UnitOfMeasurement } from './uom'

export const RAW_MATERIAL_CATEGORIES = [
  'coffee_beans',
  'dairy',
  'syrup',
  'powder',
  'topping',
  'tea',
  'packaging',
  'other',
] as const

export type RawMaterialCategory = (typeof RAW_MATERIAL_CATEGORIES)[number]

export const RAW_MATERIAL_CATEGORY_LABELS: Record<RawMaterialCategory, string> = {
  coffee_beans: 'Biji Kopi',
  dairy: 'Susu & Dairy',
  syrup: 'Sirup & Pemanis',
  powder: 'Bubuk / Powder',
  topping: 'Topping & Isian',
  tea: 'Teh & Lainnya',
  packaging: 'Kemasan',
  other: 'Lain-lain',
}

export const RAW_MATERIAL_KINDS = ['purchased', 'ingredient', 'direct'] as const

export type RawMaterialKind = (typeof RAW_MATERIAL_KINDS)[number]

export const RAW_MATERIAL_KIND_LABELS: Record<RawMaterialKind, string> = {
  purchased: 'Barang Beli',
  ingredient: 'Bahan Resep',
  direct: 'Langsung Pakai',
}

export interface RawMaterial {
  id: string
  workspaceId: string
  name: string
  sku?: string | null
  category: RawMaterialCategory
  kind: RawMaterialKind
  uomId: string
  uom?: UnitOfMeasurement | null
  /** purchased only: the ingredient one package opens into. */
  ingredientId?: string | null
  ingredient?: { id: string; name: string; uom?: UnitOfMeasurement | null } | null
  /** purchased only: ingredient quantity per package, derived from the unit conversion (read-only). */
  packQty?: number | null
  costPerUnit: number | null
  minStock?: number | null
  notes?: string | null
  isActive: boolean
  hasMovements?: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateRawMaterialReq {
  name: string
  sku?: string | null
  category: RawMaterialCategory
  kind: RawMaterialKind
  uomId: string
  ingredientId?: string | null
  minStock?: number | null
  notes?: string | null
  // Only honored for actors with canSwitchWorkspace — everyone else always
  // creates in their own workspace regardless of what's sent here.
  workspaceId?: string
}

export interface UpdateRawMaterialReq {
  id?: string
  name?: string
  sku?: string | null
  category?: RawMaterialCategory
  kind?: RawMaterialKind
  uomId?: string
  ingredientId?: string | null
  minStock?: number | null
  notes?: string | null
  isActive?: boolean
}

export interface GetRawMaterialReq {
  q?: string
  category?: RawMaterialCategory
  kinds?: RawMaterialKind[]
  ingredientId?: string
  isActive?: boolean
  pagination?: PaginationQuery
}

export interface RawMaterialList {
  items: RawMaterial[]
  pagination: PaginationMetadata
}

export interface RawMaterialUsageSummary {
  inUse: boolean
  stockBalance: number
  movementCount: number
  recipeCount: number
  draftExpenseCount: number
  usages: { name: string; count: number }[]
  reason?: string
}

